import React, { useEffect, useState } from "react";
import { Briefcase, Copy, Check, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const Copyable = ({ value, label, mono = true, testid }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success(`${label} copied`);
        setTimeout(() => setCopied(false), 1500);
      }}
      data-testid={testid}
      className="group inline-flex items-center gap-2 hover:text-[#ff9a2f] transition"
      title={`Copy ${label}`}
    >
      <span className={mono ? "font-mono text-[#f5f5f5]" : "text-[#f5f5f5]"}>{value}</span>
      {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={13} className="text-[#8a8a8a] group-hover:text-[#ff9a2f]" />}
    </button>
  );
};

const PasswordField = ({ value, testid }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="inline-flex items-center gap-2">
      <Copyable value={show ? value : "•".repeat(Math.min(12, value.length))} label="Password" testid={`${testid}-copy`} />
      <button onClick={() => setShow(!show)} className="text-[#8a8a8a] hover:text-[#ff9a2f]" data-testid={`${testid}-toggle`}>
        {show ? <EyeOff size={13} /> : <Eye size={13} />}
      </button>
    </div>
  );
};

export const AccountsPage = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data || [])).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-[#8a8a8a]" data-testid="accounts-loading">Loading accounts...</div>;

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div>
        <h1 className="font-display text-3xl font-bold">Trading Accounts</h1>
        <p className="text-[#8a8a8a] text-sm mt-1">Your assigned evaluation and funded accounts.</p>
      </div>
      {accounts.length === 0 ? (
        <div className="glass rounded-xl p-10 text-center">
          <Briefcase className="mx-auto text-[#ff7a00]" size={36} />
          <div className="mt-4 font-display text-xl font-semibold">No accounts assigned yet.</div>
          <p className="text-[#8a8a8a] text-sm mt-2">Purchase a challenge and our team will provision your trading account.</p>
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          {accounts.map((a) => (
            <div key={a.id} className="glass rounded-xl p-6 hover:border-[#ff7a00]/40 transition" data-testid={`account-card-${a.id}`}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-widest">{a.challenge_type} · {a.phase}</div>
                  <div className="font-display text-2xl font-bold mt-1">{a.platform}</div>
                  <div className="text-xs text-[#8a8a8a] mt-0.5">{a.broker}</div>
                </div>
                <Badge className={`uppercase text-[10px] tracking-widest ${
                  a.status === "Funded" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" :
                  a.status === "Passed" ? "bg-[#ff7a00]/15 text-[#ff7a00] border-[#ff7a00]/30" :
                  a.status === "Failed" ? "bg-red-500/15 text-red-400 border-red-500/30" :
                  a.status === "Suspended" ? "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" :
                  "bg-[#ff7a00]/10 text-[#ff9a2f] border-[#ff7a00]/30"
                }`} data-testid={`account-status-${a.id}`}>{a.status}</Badge>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-[#8a8a8a] text-xs uppercase tracking-wider">Account Size</span>
                  <span className="font-display text-lg font-bold text-[#ff7a00]">${a.account_size.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#8a8a8a] text-xs uppercase tracking-wider">Login ID</span>
                  <Copyable value={a.login_id} label="Login ID" testid={`account-login-${a.id}`} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#8a8a8a] text-xs uppercase tracking-wider">Password</span>
                  <PasswordField value={a.password} testid={`account-password-${a.id}`} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#8a8a8a] text-xs uppercase tracking-wider">Server</span>
                  <Copyable value={a.server} label="Server" testid={`account-server-${a.id}`} />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[#8a8a8a] text-xs uppercase tracking-wider">Leverage</span>
                  <span className="text-[#f5f5f5]">{a.leverage}</span>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-[#ff7a00]/15 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Target</div>
                  <div className="font-semibold">{a.profit_target_pct}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Daily DD</div>
                  <div className="font-semibold">{a.daily_drawdown_pct}%</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Max DD</div>
                  <div className="font-semibold">{a.max_drawdown_pct}%</div>
                </div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Balance</div>
                  <div className="font-semibold text-[#f5f5f5]">${(a.current_balance || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">Equity</div>
                  <div className="font-semibold text-[#f5f5f5]">${(a.current_equity || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#8a8a8a] uppercase tracking-wider">P&amp;L</div>
                  <div className={`font-semibold ${(a.current_pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                    {(a.current_pnl || 0) >= 0 ? "+" : ""}${(a.current_pnl || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
